import Link from "next/link"
import { Alert } from "@ui/alert"
import { PageBlock } from "@ui/page-block"
import { Heading } from "@ui/heading"
import { Stack } from "@ui/stack"
import { InputField } from "@ui/input"
import { TopbarLayout } from "@layouts/topbar"
import { GetServerSideProps } from "next"
import { createServerClient } from "@graph/client"
import { gql, useMutation } from "@apollo/client"
import { Field, Form } from "@graph/form"
import { Fieldset } from "@ui/fieldset"
import { Button } from "@ui/button"
import AUTH_STATE_QUERY from "@graph/queries/auth-state.gql"
import {
  SendResetPasswordInstructions,
  SendResetPasswordInstructionsVariables,
} from "@graph/types/SendResetPasswordInstructions"
import { AuthStateQuery } from "@graph/types/AuthStateQuery"
import { AuthState } from "@graph/types/globalTypes"
import { loginPath, signupPath } from "@app/routes"
import { required } from "validation"
import { ServerResponse } from "@server/response"
import { PageProps } from "@server/props"
import { Card } from "@ui/card"
import { Row } from "@ui/row"

export default function LoginPage() {
  const [sendResetPasswordInstructions] = useSendResetPasswordInstructions()

  return (
    <TopbarLayout title="Send reset password instructions">
      <PageBlock>
        <Row>
          <Row.Column variant="primary" gap="6">
            <Form
              onSubmit={async ({ email }) => {
                if (!email) return

                const { data } = await sendResetPasswordInstructions({
                  variables: { email },
                })

                if (!data) {
                  return { allFields: "Something went wrong. Please try again or contact support." }
                }

                return data.sendResetPasswordInstructions?.errors
              }}
            >
              {({ handleSubmit, submitting, submitSucceeded }) => (
                <Card as="form" onSubmit={handleSubmit}>
                  <Heading as="h1" level="2">
                    Reset your password
                  </Heading>
                  <Fieldset width={["100%", "page.half"]}>
                    <Field
                      id="email"
                      name="email"
                      label="Email"
                      required
                      autoFocus
                      component={InputField}
                      validate={required}
                    />
                    <Button id="submit-send-reset-password-instructions" fluid={[true, false]} loading={submitting}>
                      Send reset password instructions
                    </Button>
                    {submitSucceeded && (
                      <Alert title="Success" variant="success">
                        Please check your email for instructions on how to reset your password.
                      </Alert>
                    )}
                  </Fieldset>
                </Card>
              )}
            </Form>

            <Stack gap="3">
              <Link href={loginPath} passHref>
                <a>Login</a>
              </Link>
              <Link href={signupPath} passHref>
                <a>Sign up</a>
              </Link>
            </Stack>
          </Row.Column>
        </Row>
      </PageBlock>
    </TopbarLayout>
  )
}
function useSendResetPasswordInstructions() {
  return useMutation<SendResetPasswordInstructions, SendResetPasswordInstructionsVariables>(gql`
    mutation SendResetPasswordInstructions($email: String!) {
      sendResetPasswordInstructions(input: { email: $email }) {
        errors {
          allFields
          fields {
            name
            error
          }
        }
      }
    }
  `)
}

export const getServerSideProps: GetServerSideProps<PageProps> = async (ctx) => {
  const client = createServerClient({ headers: ctx.req.headers })

  const { data } = await client.query<AuthStateQuery>({
    query: AUTH_STATE_QUERY,
  })

  if (data.authState === AuthState.logged_in) {
    return ServerResponse.redirect("/")
  }

  return { props: data }
}
